import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
/******************************************************************************
 * Copyright (c) 2026 Contributors to the Eclipse Foundation.
 *
 * See the NOTICE file(s) distributed with this work for additional
 * information regarding copyright ownership.
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * https://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *****************************************************************************/
import { useContext, useEffect, useState } from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, LinearProgress, Link, MenuItem, TextField, Typography } from '@mui/material';
import { handleError } from '../../../utils';
import { MainContext } from '../../../context';
import { TrustedPublisherProviderIcon } from './trusted-publisher-provider-icon';
import { findRegistrationPathPair, orderRegistrationKeys, providerKind } from './registration-fields';
import { useRegisterTrustedPublisher } from './use-trusted-publishers';
// hidden when no documentation URL is configured in the page settings
export const TrustedPublishingDocsLink = () => {
    const { pageSettings } = useContext(MainContext);
    const url = pageSettings.urls.trustedPublishing;
    if (!url) {
        return null;
    }
    return (_jsx(Link, { color: 'secondary', href: url, target: '_blank', rel: 'noopener', children: "Learn more about trusted publishing" }));
};
// Optional helper text per provider kind and registration key; unlisted fields get none.
const FIELD_HELP = {
    github: {
        workflow: 'Filename only (e.g. release.yml). Must exist in .github/workflows/ in your repository.',
        environment: (_jsxs(_Fragment, { children: ["The name of the", ' ', _jsx(Link, { href: 'https://docs.github.com/en/actions/managing-workflow-runs-and-deployments/managing-deployments/managing-environments-for-deployment', target: '_blank', rel: 'noopener', color: 'secondary', children: "GitHub Actions environment" }), ' ', "used for publishing. This is encouraged for projects with maintainers who should not have publishing access."] }))
    },
    gitlab: {
        workflow: 'CI config file name (e.g. .gitlab-ci.yml).',
        environment: (_jsxs(_Fragment, { children: ["The name of the", ' ', _jsx(Link, { href: 'https://docs.gitlab.com/ee/ci/environments/', target: '_blank', rel: 'noopener', color: 'secondary', children: "GitLab CI/CD environment" }), ' ', "used for publishing. This is encouraged for projects with maintainers who should not have publishing access."] }))
    }
};
const fieldHelp = (providerId, key) => FIELD_HELP[providerKind(providerId)]?.[key];
export const RegisterTrustedPublisherDialog = props => {
    const { open, initialProvider, lockedExtension } = props;
    const { mutateAsync: registerTrustedPublisher, isPending: loading, error: registerError, reset: resetRegistration } = useRegisterTrustedPublisher();
    // server-side validation errors (e.g. repository not found) show inside the form
    const errorMessage = registerError ? handleError(registerError) : undefined;
    const [namespaceName, setNamespaceName] = useState('');
    const [provider, setProvider] = useState('');
    const [extension, setExtension] = useState('');
    const [registration, setRegistration] = useState({});
    useEffect(() => {
        if (open) {
            setNamespaceName(props.namespace ?? '');
            setProvider(initialProvider ?? '');
            setExtension(lockedExtension ?? '');
            setRegistration({});
            resetRegistration();
        }
    }, [open, initialProvider, lockedExtension, props.namespace]);
    const namespace = props.namespace ?? namespaceName;
    const selectedNamespace = props.namespaces.find(ns => ns.name === namespace);
    const extensionNames = selectedNamespace?.registrableExtensions ?? [];
    const handleClose = () => {
        if (!loading) {
            props.onClose();
        }
    };
    const inputs = props.providers.find(p => p.id === provider)?.registrationInputs ?? [];
    const inputsByKey = new Map(inputs.map(input => [input.key, input]));
    const orderedKeys = orderRegistrationKeys(inputs.map(input => input.key));
    const requiredKeys = orderedKeys.filter(key => !inputsByKey.get(key)?.optional);
    const pathPair = findRegistrationPathPair(orderedKeys);
    const [pathOwnerKey, pathRepoKey] = pathPair ?? [];
    const standaloneKeys = orderedKeys.filter(key => !pathPair?.includes(key));
    const registrationField = (key, flex) => {
        const input = inputsByKey.get(key);
        return (_jsx(TextField, { required: !input?.optional, fullWidth: flex === undefined, label: input?.description, helperText: fieldHelp(provider, key), value: registration[key] ?? '', onChange: e => setRegistration(prev => ({ ...prev, [key]: e.target.value })), sx: flex !== undefined ? { flex } : undefined }, key));
    };
    const canRegister = !loading &&
        namespace !== '' &&
        provider !== '' &&
        extension !== '' &&
        requiredKeys.every(key => (registration[key] ?? '').trim() !== '');
    const handleRegister = async (event) => {
        event.preventDefault();
        const cleaned = {};
        for (const key of orderedKeys) {
            const value = (registration[key] ?? '').trim();
            if (value !== '') {
                cleaned[key] = value;
            }
        }
        const trustedPublishingUrl = selectedNamespace?.trustedPublishingUrl;
        if (!trustedPublishingUrl) {
            return;
        }
        try {
            await registerTrustedPublisher({
                trustedPublishingUrl,
                request: { provider, namespace, extension, registration: cleaned }
            });
            props.onRegistered?.();
            props.onClose();
        }
        catch {
            // shown inline via the mutation's error state
        }
    };
    // Why nothing can be registered: either everything is taken (one publisher per extension) or
    // there is nothing to bind to. Extensions must exist and be active, which the server enforces.
    const nothingRegistrableText = () => {
        if ((selectedNamespace?.registeredExtensions.length ?? 0) === 0) {
            return 'This namespace has no active extensions yet. Publish an extension before registering a trusted publisher.';
        }
        return lockedExtension
            ? 'This extension already has a trusted publisher. Delete the existing registration to replace it.'
            : 'Every active extension in this namespace already has a trusted publisher. Delete an existing registration to replace it.';
    };
    // Provider and registration fields for the chosen namespace; only rendered once one is picked.
    const renderFormBody = () => {
        if (namespace === '') {
            return null;
        }
        if (extensionNames.length === 0) {
            return _jsx(DialogContentText, { color: 'error', children: nothingRegistrableText() });
        }
        return (_jsxs(_Fragment, { children: [_jsx(TextField, { select: true, required: true, fullWidth: true, label: 'Publisher', value: provider, onChange: e => {
                        setProvider(e.target.value);
                        setRegistration({});
                    }, children: props.providers.map(p => (_jsx(MenuItem, { value: p.id, children: _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1 }, children: [_jsx(TrustedPublisherProviderIcon, { providerId: p.id, fontSize: 'small' }), p.name] }) }, p.id))) }), !lockedExtension && (_jsx(TextField, { select: true, required: true, fullWidth: true, label: 'Extension', value: extension, onChange: e => setExtension(e.target.value), children: extensionNames.map(name => (_jsx(MenuItem, { value: name, children: name }, name))) })), pathOwnerKey && pathRepoKey && (_jsxs(Box, { sx: { display: 'flex', alignItems: 'flex-start', gap: 1 }, children: [registrationField(pathOwnerKey, 1), _jsx(Typography, { sx: { mt: 2 }, children: "/" }), registrationField(pathRepoKey, 2)] })), standaloneKeys.map(key => registrationField(key))] }));
    };
    return (_jsxs(Dialog, { open: open, onClose: handleClose, fullWidth: true, maxWidth: 'sm', PaperProps: { component: 'form', onSubmit: handleRegister }, children: [_jsx(DialogTitle, { children: "Register trusted publisher" }), loading && _jsx(LinearProgress, { color: 'secondary' }), _jsxs(DialogContent, { children: [_jsxs(DialogContentText, { sx: { mb: 3 }, children: ["Establish trust between this extension and your repository using OpenID Connect (OIDC), so a CI workflow can publish without a personal access token. Publishing is allowed from any branch or tag.", ' ', _jsx(TrustedPublishingDocsLink, {})] }), errorMessage && (_jsx(Alert, { severity: 'error', sx: { mb: 3 }, children: errorMessage })), _jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 3 }, children: [!props.namespace && (_jsx(TextField, { select: true, required: true, fullWidth: true, label: 'Namespace', value: namespaceName, onChange: e => {
                                    setNamespaceName(e.target.value);
                                    setExtension('');
                                }, children: props.namespaces.map(ns => (_jsx(MenuItem, { value: ns.name, children: ns.name }, ns.name))) })), renderFormBody()] })] }), _jsxs(DialogActions, { children: [_jsx(Button, { onClick: handleClose, children: "Cancel" }), _jsx(Button, { type: 'submit', variant: 'contained', color: 'secondary', disabled: !canRegister, children: "Register" })] })] }));
};
//# sourceMappingURL=register-trusted-publisher-dialog.js.map